import { v7 as uuidv7 } from 'uuid';
import { db } from './localDB';
import {
  AccountOwner,
  buildUploadQueue,
  LOCAL_DISPLAY_NAME,
  LOCAL_USER_PREFIX,
  LocalDataCount,
  LocalIdentity,
  rekeyLocalData,
} from '../domain';

const IDENTITY_KEY = 'localIdentity';

/** The local user (docs/plans/local-mode.md) and carrying what they made into an account. */
export class LocalModeService {
  async getIdentity(): Promise<LocalIdentity | null> {
    const setting = await db.settings.get(IDENTITY_KEY);
    return setting ? (JSON.parse(setting.value) as LocalIdentity) : null;
  }

  /** The local user kept on this phone, made the first time it's asked for. Never replaced while
   * it's there: the households made in local mode are found by its id, so a new one would hide
   * them. It goes only when its data has been carried into an account (moveToAccount). */
  async getOrCreateIdentity(): Promise<LocalIdentity> {
    return db.transaction('rw', db.settings, async () => {
      const kept = await this.getIdentity();
      if (kept) return kept;
      const identity: LocalIdentity = {
        id: `${LOCAL_USER_PREFIX}${uuidv7()}`,
        displayName: LOCAL_DISPLAY_NAME,
        createdAt: new Date().toISOString(),
      };
      await db.settings.put({ key: IDENTITY_KEY, value: JSON.stringify(identity) });
      return identity;
    });
  }

  /** How much the local user has made: what a signed-in user is asked whether to add to their
   * account. Nothing (zeros) without a local user. */
  async countLocalData(): Promise<LocalDataCount> {
    const identity = await this.getIdentity();
    if (!identity) return { households: 0, chores: 0 };
    const householdIds = await db.households.filter((h) => h.member_ids.includes(identity.id)).primaryKeys();
    const chores = householdIds.length > 0 ? await db.chores.where('household_id').anyOf(householdIds).count() : 0;
    return { households: householdIds.length, chores };
  }

  /** Deletes everything the local user made — households, chores, completions, what's queued for
   * them — and the local user with it; signing out later starts a new, empty one. One transaction,
   * so it's all gone or none of it. */
  async deleteLocalData(): Promise<void> {
    await db.transaction('rw', [db.settings, db.households, db.chores, db.activities, db.offlineQueue], async () => {
      const identity = await this.getIdentity();
      if (!identity) return;
      const householdIds = await db.households.filter((h) => h.member_ids.includes(identity.id)).primaryKeys();
      const choreIds = await db.chores.where('household_id').anyOf(householdIds).primaryKeys();
      const local = new Set(householdIds);
      await db.offlineQueue.filter((item) => item.householdId !== undefined && local.has(item.householdId)).delete();
      await db.activities.where('chore_id').anyOf(choreIds).delete();
      await db.chores.bulkDelete(choreIds);
      await db.households.bulkDelete(householdIds);
      await db.settings.delete(IDENTITY_KEY);
    });
  }

  /**
   * Makes everything the local user made the account's, and queues it to be pushed; true if there
   * was a local user to do it for. One transaction: interrupted (the tab closed), it has either
   * happened or not, and the local identity still being there on the next start means "not yet".
   *
   * The queue's actions for local households are replaced by ones built from what's there now
   * (buildUploadQueue), so the history of local mode isn't replayed. Anything else in the queue —
   * an account's actions not yet pushed when it signed out — is kept, after them.
   *
   * Until those actions are pushed, a full sync leaves the households alone (findStaleIds spares
   * whatever the queue refers to), so the server not having them yet removes nothing.
   */
  async moveToAccount(account: AccountOwner): Promise<boolean> {
    return db.transaction('rw', [db.settings, db.households, db.chores, db.activities, db.offlineQueue], async () => {
      const identity = await this.getIdentity();
      if (!identity) return false;

      const households = await db.households.filter((h) => h.member_ids.includes(identity.id)).toArray();
      const householdIds = households.map((h) => h.id);
      const chores = await db.chores.where('household_id').anyOf(householdIds).toArray();
      const activities = await db.activities.where('chore_id').anyOf(chores.map((c) => c.id)).toArray();

      const moved = rekeyLocalData({ households, chores, activities }, identity.id, account);
      await db.households.bulkPut(moved.households);
      await db.chores.bulkPut(moved.chores);
      await db.activities.bulkPut(moved.activities);

      // The uploads go first. What else is queued — an earlier account's actions not pushed when
      // it signed out — may be refused for this account, and a sync stops at the first refusal;
      // ahead of them, the uploads are pushed before that.
      const local = new Set(householdIds);
      const others = await db.offlineQueue.filter((item) => item.householdId === undefined || !local.has(item.householdId)).toArray();
      await db.offlineQueue.clear();
      await db.offlineQueue.bulkAdd(buildUploadQueue(moved, new Date().toISOString()));
      // Added again without their ids, so they're numbered after the uploads.
      await db.offlineQueue.bulkAdd(others.map((item) => ({ ...item, id: undefined })));

      await db.settings.delete(IDENTITY_KEY);
      return true;
    });
  }
}

export const localModeService = new LocalModeService();
